import { useEffect, useRef } from 'react'
import EventForm from './forms/event'
import WeddingForm from './forms/wedding'
import OutOfZoneForm from './forms/out-of-zone'

// schema matching weddings notion db as initial form state
const weddingFormSchema = {
  'First Name': '',
  'Last Name': '',
  'Phone Number': '',
  Email: '',
  Services: [],
  'Type of Services': [],
  'Wedding Date': '', // ISO date string
  'Trial Date': '', // ISO date string
  'Number of People': '',
  Address: '',
  'Booking Notes': '',
  'Wedding Planner Name': '',
  'Wedding Planner Email': '',
  Attribution: '',
  'Is Complete': false,
  'Date Status': true
}

// schema matching events notion db as initial form state
const eventFormSchema = {
  'First Name': '',
  'Last Name': '',
  'Phone Number': '',
  Email: '',
  'Brand Affiliation': '',
  Services: [],
  'Type of Services': [],
  'Number of People': '',
  'Event Date': '', // ISO string
  'Event Location': '',
  'Booking Notes': '',
  Attribution: '',
  'Is Complete': false
}

const outOfZoneSchema = {
  'First Name': '',
  'Last Name': '',
  'Phone Number': '',
  Email: '',
  Services: [],
  'Type of Services': [],
  Address: '',
  'Number of People': '',
  'Service Date': '', // ISO string
  'Booking Notes': '',
}

// returns notion schema that can be used as forms initial state
export const getSchema = (formName) => {
  switch (formName) {
    case 'event':
      return eventFormSchema
    case 'wedding':
      return weddingFormSchema
    case 'out-of-zone':
      return outOfZoneSchema
    default:
      return {}
  }
}

// returns form component to display from formName prop
export const getFormComponent = (formName) => {
  switch (formName) {
    case 'event':
      return EventForm
    case 'wedding':
      return WeddingForm
    case 'out-of-zone':
      return OutOfZoneForm
    default:
      return null
  }
}

/**
 * THIS FUNCTION IS UN-OPTIMIZED AND REPEATS ITSELF ON PURPOSE
 * while there are several overlapping fields in both forms,
 * the errors need to be added in page order from top to bottom
 * for the scroll-to-first-error functionality to work properly.
 * @param {*} values
 * @param {*} formName
 * @returns
 */
export const validate = (values, formName) => {
  const errors = {}

  if (formName === 'out-of-zone') {
    if (!values['First Name']) {
      errors['First Name'] = 'Please enter a first name.'
    }

    if (!values['Last Name']) {
      errors['Last Name'] = 'Please enter a last name.'
    }

    if (!values['Phone Number']) {
      errors['Phone Number'] = 'Please enter a phone number'
    }

    if (!values.Email) {
      errors.Email = 'Please enter an email address.'
    }

    if (!values['Services'].length) {
      errors['Services'] = 'Select at least 1 service.'
    }

    if (!values['Type of Services'].length) {
      errors['Type of Services'] = 'Please select at least 1 type of service.'
    }

    if (!values['Address']) {
      errors['Address'] = 'Please enter an address or select a location.'
    }

    if (!values['Number of People']) {
      errors['Number of People'] = 'Please select number of people.'
    }

    if (!values['Event Date']) {
      errors['Event Date'] = 'Please select a date.'
    }
  }

  if (formName === 'event') {
    if (!values['First Name']) {
      errors['First Name'] = 'Please enter a first name.'
    }

    if (!values['Last Name']) {
      errors['Last Name'] = 'Please enter a last name.'
    }

    if (!values['Phone Number']) {
      errors['Phone Number'] = 'Please enter a phone number'
    }

    if (!values.Email) {
      errors.Email = 'Please enter an email address.'
    }

    if (!values['Services'].length) {
      errors['Services'] = 'Select at least 1 service.'
    }

    if (!values['Type of Services'].length) {
      errors['Type of Services'] = 'Please select at least 1 type of service.'
    }

    if (!values['Event Location']) {
      errors['Event Location'] = 'Please enter an address or select a location.'
    }

    if (!values['Number of People']) {
      errors['Number of People'] = 'Please select number of people.'
    }

    if (!values['Event Date']) {
      errors['Event Date'] = 'Please select a date.'
    }

    if (!values['Attribution']) {
      errors['Attribution'] = 'Please select an option.'
    }
  }

  if (formName === 'wedding') {
    if (!values['First Name']) {
      errors['First Name'] = 'Please enter a first name.'
    }

    if (!values['Last Name']) {
      errors['Last Name'] = 'Please enter a last name.'
    }

    if (!values['Phone Number']) {
      errors['Phone Number'] = 'Please enter a phone number'
    }

    if (!values.Email) {
      errors.Email = 'Please enter an email address.'
    }

    if (!values['Person Booking']) {
      errors['Person Booking'] = 'Please select the person booking.'
    }

    if (!values['Services'].length) {
      errors['Services'] = 'Select at least 1 service.'
    }

    if (!values['Type of Services'].length) {
      errors['Type of Services'] = 'Please select at least 1 type of service.'
    }

    if (!values['Address']) {
      errors['Address'] = 'Please enter an address or select a location.'
    }

    if (values['Date Status'] && !values['Wedding Date']) {
      errors['Wedding Date'] = 'Please select a date.'
    }

    if (!values['Number of People']) {
      errors['Number of People'] = 'Please select number of people.'
    }

    if (!values['Attribution']) {
      errors['Attribution'] = 'Please select how you heard about us.'
    }
  }

  return errors
}

/* useScrollToFirstError
 * @param {form}: React final form to subscribe to
 * @param {fieldRefs}: Array of refs to fields to autoscroll to
 */
export const useScrollToFirstError = (form, fieldRefs) => {
  const hasScrolled = useRef(false)

  useEffect(() => {
    const unsubscribe = form.subscribe(
      (formState) => {
        if (formState.submitFailed && !hasScrolled.current) {
          const firstErrorField = Object.keys(formState.errors)[0]

          const el = fieldRefs.current[firstErrorField]

          if (el?.scrollIntoView) {
            hasScrolled.current = true
            el.scrollIntoView({ behavior: 'smooth', block: 'center' })
            el.focus?.()
          }

          if (!formState.submitFailed) {
            hasScrolled.current = false
          }
        }
      },
      { submitFailed: true, errors: true },
    )

    return () => {
      unsubscribe()
    }
  }, [form, fieldRefs])
}
